import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLink, faSave, faSpinner, faPaperPlane, faTrashAlt,
  faCheckCircle, faCopy, faExclamationTriangle, faPlus,
  faCog, faChevronDown, faChevronUp, faEye, faChartBar,
  faBullhorn, faArrowUp, faArrowDown, faPencilAlt, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { Alert, Badge } from 'react-bootstrap';
import { format } from 'date-fns';
import './NewsletterTab.css';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PAGE_SIZE = 15;
const SUBSCRIBERS_FETCH_LIMIT = 100;
const SUBSCRIBERS_MAX = 2000;

function NewsletterTab() {
  const { currentUser, userOrgId, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Settings state
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Form state (separate from saved config for dirty tracking)
  const [formEnabled, setFormEnabled] = useState(false);
  const [formSendDay, setFormSendDay] = useState(5);
  const [formSendHour, setFormSendHour] = useState(9);
  const [formLookaheadDays, setFormLookaheadDays] = useState(14);
  const [formCustomIntro, setFormCustomIntro] = useState('');
  const [formCustomFooter, setFormCustomFooter] = useState('');
  const [formReplyToEmail, setFormReplyToEmail] = useState('');
  const [formProofEnabled, setFormProofEnabled] = useState(false);
  const [formProofLeadHours, setFormProofLeadHours] = useState(24);

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersCapped, setSubscribersCapped] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmingIds, setConfirmingIds] = useState(() => new Set());

  // Subscriber search/filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [memberFilter, setMemberFilter] = useState('all'); // 'all' | 'member' | 'nonmember'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'confirmed' | 'pending'

  // History state
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLastDocId, setHistoryLastDocId] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);

  // History detail view state
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyEventsLoading, setHistoryEventsLoading] = useState(false);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState(null); // null = closed, {} = new, {id, title, body} = editing
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Signup link & org info
  const [orgSlug, setOrgSlug] = useState('');
  const [orgTimezone, setOrgTimezone] = useState('');
  const [orgContactEmail, setOrgContactEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const getConfigFn = httpsCallable(functions, 'getNewsletterConfig');
      const result = await getConfigFn();
      const c = result.data.config;
      setConfig(c);
      setFormEnabled(c.enabled);
      setFormSendDay(c.sendDay);
      setFormSendHour(c.sendHour);
      setFormLookaheadDays(c.eventLookaheadDays);
      setFormCustomIntro(c.customIntro || '');
      setFormCustomFooter(c.customFooter || '');
      setFormReplyToEmail(c.replyToEmail || '');
      setFormProofEnabled(c.proofEnabled || false);
      setFormProofLeadHours(c.proofLeadHours || 24);
    } catch (err) {
      console.error('Error fetching newsletter config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser && isAdmin && userOrgId) {
      fetchConfig();
    }
  }, [currentUser, isAdmin, userOrgId, fetchConfig]);

  // Fetch org slug and timezone
  useEffect(() => {
    if (currentUser && userOrgId) {
      const fetchOrgInfo = async () => {
        try {
          const { db } = await import('../firebase');
          const { doc, getDoc } = await import('firebase/firestore');
          const orgDoc = await getDoc(doc(db, 'organization', userOrgId));
          if (orgDoc.exists()) {
            const data = orgDoc.data();
            if (data.orgSlug) setOrgSlug(data.orgSlug);
            if (data.timezone) setOrgTimezone(data.timezone);
            if (data.contactEmail) setOrgContactEmail(data.contactEmail);
          }
        } catch (err) {
          console.error('Error fetching org info:', err);
        }
      };
      fetchOrgInfo();
    }
  }, [currentUser, userOrgId]);

  // Load subscribers, history, and announcements on mount
  useEffect(() => {
    if (currentUser && isAdmin && userOrgId) {
      fetchSubscribers();
      fetchHistory(true);
      fetchAnnouncements();
    }
  }, [currentUser, isAdmin, userOrgId]);

  const handleToggle = async (newEnabled) => {
    setToggling(true);
    setError(null);
    setSuccessMsg(null);
    setFormEnabled(newEnabled);
    try {
      const updateConfigFn = httpsCallable(functions, 'updateNewsletterConfig');
      await updateConfigFn({
        enabled: newEnabled,
        sendDay: formSendDay,
        sendHour: formSendHour,
        eventLookaheadDays: formLookaheadDays,
        customIntro: formCustomIntro,
        customFooter: formCustomFooter,
        replyToEmail: formReplyToEmail,
        proofEnabled: formProofEnabled,
        proofLeadHours: formProofLeadHours,
      });
      setSuccessMsg(newEnabled ? 'Newsletter enabled.' : 'Newsletter disabled.');
      await fetchConfig();
    } catch (err) {
      console.error('Error toggling newsletter:', err);
      setFormEnabled(!newEnabled);
      setError(err.message);
    } finally {
      setToggling(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const updateConfigFn = httpsCallable(functions, 'updateNewsletterConfig');
      await updateConfigFn({
        enabled: formEnabled,
        sendDay: formSendDay,
        sendHour: formSendHour,
        eventLookaheadDays: formLookaheadDays,
        customIntro: formCustomIntro,
        customFooter: formCustomFooter,
        replyToEmail: formReplyToEmail,
        proofEnabled: formProofEnabled,
        proofLeadHours: formProofLeadHours,
      });
      setSuccessMsg('Newsletter settings saved.');
      await fetchConfig();
    } catch (err) {
      console.error('Error saving newsletter config:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setError(null);
    try {
      const sendTestFn = httpsCallable(functions, 'sendTestNewsletter');
      const result = await sendTestFn();
      setSuccessMsg(`Test newsletter sent to ${result.data.sentTo}`);
    } catch (err) {
      console.error('Error sending test newsletter:', err);
      setError(err.message);
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send the newsletter now to all confirmed subscribers?')) return;
    setSendingNow(true);
    setError(null);
    try {
      const sendNowFn = httpsCallable(functions, 'sendNewsletterNow');
      const result = await sendNowFn();
      const { sent, failed } = result.data;
      setSuccessMsg(`Newsletter sent to ${sent} subscriber${sent !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`);
      await fetchConfig();
    } catch (err) {
      console.error('Error sending newsletter:', err);
      setError(err.message);
    } finally {
      setSendingNow(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const previewFn = httpsCallable(functions, 'previewNewsletter');
      const result = await previewFn();
      setPreviewHtml(result.data.html);
    } catch (err) {
      console.error('Error loading newsletter preview:', err);
      setError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleIframeLoad = (e) => {
    const iframe = e.target;
    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        const height = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        iframe.style.height = height + 'px';
      } catch {
        // cross-origin fallback — shouldn't happen with srcDoc
      }
    };
    resize();
    // Re-measure after images and styles have settled
    setTimeout(resize, 100);
  };

  const fetchSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const getSubsFn = httpsCallable(functions, 'getNewsletterSubscribers');
      let all = [];
      let cursor;
      let hasMore = true;
      let capped = false;
      while (hasMore && all.length < SUBSCRIBERS_MAX) {
        const result = await getSubsFn({ limit: SUBSCRIBERS_FETCH_LIMIT, startAfter: cursor });
        const data = result.data;
        all = all.concat(data.subscribers);
        cursor = data.lastDocId;
        hasMore = data.hasMore;
        if (hasMore && all.length >= SUBSCRIBERS_MAX) {
          capped = true;
        }
      }
      setSubscribers(all);
      setSubscribersCapped(capped);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err.message);
    } finally {
      setSubscribersLoading(false);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const addFn = httpsCallable(functions, 'addNewsletterSubscriber');
      await addFn({
        email: addEmail.trim(),
        firstName: addFirstName.trim() || undefined,
        lastName: addLastName.trim() || undefined,
      });
      setSuccessMsg('Subscriber added.');
      setAddEmail('');
      setAddFirstName('');
      setAddLastName('');
      await fetchSubscribers();
      await fetchConfig();
    } catch (err) {
      console.error('Error adding subscriber:', err);
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSubscriber = async (subscriberId) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      const removeFn = httpsCallable(functions, 'removeNewsletterSubscriber');
      await removeFn({ subscriberId });
      setSubscribers(prev => prev.filter(s => s.id !== subscriberId));
      setSuccessMsg('Subscriber removed.');
    } catch (err) {
      console.error('Error removing subscriber:', err);
      setError(err.message);
    }
  };

  const handleConfirmSubscriber = async (subscriberId) => {
    setConfirmingIds(prev => new Set(prev).add(subscriberId));
    setError(null);
    try {
      const confirmFn = httpsCallable(functions, 'confirmNewsletterSubscriber');
      const result = await confirmFn({ subscriberId });
      const memberId = result.data?.memberId;
      setSubscribers(prev => prev.map(s =>
        s.id === subscriberId
          ? { ...s, status: 'confirmed', ...(memberId ? { memberId } : {}) }
          : s
      ));
      await fetchConfig();
      setSuccessMsg('Subscriber confirmed.');
    } catch (err) {
      console.error('Error confirming subscriber:', err);
      setError(err.message);
    } finally {
      setConfirmingIds(prev => {
        const next = new Set(prev);
        next.delete(subscriberId);
        return next;
      });
    }
  };

  const filteredSubscribers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return subscribers.filter(sub => {
      if (term) {
        const emailMatch = sub.email?.toLowerCase().includes(term);
        const fullName = [sub.firstName, sub.lastName].filter(Boolean).join(' ').toLowerCase();
        const nameMatch = fullName.includes(term);
        if (!emailMatch && !nameMatch) return false;
      }
      if (memberFilter === 'member' && !sub.memberId) return false;
      if (memberFilter === 'nonmember' && sub.memberId) return false;
      if (statusFilter !== 'all' && sub.status !== statusFilter) return false;
      return true;
    });
  }, [subscribers, searchTerm, memberFilter, statusFilter]);

  const clearSubscriberFilters = () => {
    setSearchTerm('');
    setMemberFilter('all');
    setStatusFilter('all');
  };

  const handleToggleProofreader = async (subscriberId, currentValue) => {
    const newValue = !currentValue;
    // Optimistic update
    setSubscribers(prev => prev.map(s =>
      s.id === subscriberId ? { ...s, proofreader: newValue } : s
    ));
    try {
      const toggleFn = httpsCallable(functions, 'toggleProofreader');
      await toggleFn({ subscriberId, proofreader: newValue });
    } catch (err) {
      console.error('Error toggling proofreader:', err);
      setError(err.message);
      // Revert on error
      setSubscribers(prev => prev.map(s =>
        s.id === subscriberId ? { ...s, proofreader: currentValue } : s
      ));
    }
  };

  const fetchHistory = async (reset = false) => {
    setHistoryLoading(true);
    try {
      const getHistoryFn = httpsCallable(functions, 'getNewsletterHistory');
      const result = await getHistoryFn({
        limit: PAGE_SIZE,
        startAfter: reset ? undefined : historyLastDocId,
      });
      const data = result.data;
      if (reset) {
        setHistoryItems(data.history);
      } else {
        setHistoryItems(prev => [...prev, ...data.history]);
      }
      setHistoryLastDocId(data.lastDocId);
      setHistoryHasMore(data.hasMore);
    } catch (err) {
      console.error('Error fetching newsletter history:', err);
      setError(err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleToggleHistoryDetail = async (historyId) => {
    if (expandedHistoryId === historyId) {
      setExpandedHistoryId(null);
      setHistoryEvents([]);
      return;
    }
    setExpandedHistoryId(historyId);
    setHistoryEvents([]);
    setHistoryEventsLoading(true);
    try {
      const getEventsFn = httpsCallable(functions, 'getNewsletterEvents');
      const result = await getEventsFn({ historyId, limit: 200 });
      setHistoryEvents(result.data.events || []);
    } catch (err) {
      console.error('Error fetching newsletter events:', err);
      setError(err.message);
    } finally {
      setHistoryEventsLoading(false);
    }
  };

  const buildRecipientSummary = (events) => {
    const byEmail = {};
    for (const evt of events) {
      if (!byEmail[evt.email]) {
        byEmail[evt.email] = { email: evt.email, delivered: false, opened: false, clicked: false, bounced: false, unsubscribed: false };
      }
      const r = byEmail[evt.email];
      if (evt.event === 'delivered') r.delivered = true;
      if (evt.event === 'open') r.opened = true;
      if (evt.event === 'click') r.clicked = true;
      if (evt.event === 'bounce') r.bounced = true;
      if (evt.event === 'unsubscribe' || evt.event === 'group_unsubscribe') r.unsubscribed = true;
    }
    return Object.values(byEmail).sort((a, b) => a.email.localeCompare(b.email));
  };

  const handleCopyLink = () => {
    const url = `https://www.churchaware.com/newsletter/${orgSlug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const getAnnouncementsFn = httpsCallable(functions, 'getNewsletterAnnouncements');
      const result = await getAnnouncementsFn();
      setAnnouncements(result.data.announcements || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError(err.message);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcementForm || !announcementForm.title?.trim() || !announcementForm.body?.trim()) return;
    setAnnouncementSaving(true);
    setError(null);
    try {
      const saveFn = httpsCallable(functions, 'saveNewsletterAnnouncement');
      await saveFn({
        id: announcementForm.id || undefined,
        title: announcementForm.title,
        body: announcementForm.body,
        active: announcementForm.active !== undefined ? announcementForm.active : true,
        expiresAt: announcementForm.expiresAt || null,
      });
      setSuccessMsg(announcementForm.id ? 'Announcement updated.' : 'Announcement added.');
      setAnnouncementForm(null);
      await fetchAnnouncements();
    } catch (err) {
      console.error('Error saving announcement:', err);
      setError(err.message);
    } finally {
      setAnnouncementSaving(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    setError(null);
    try {
      const deleteFn = httpsCallable(functions, 'deleteNewsletterAnnouncement');
      await deleteFn({ id });
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      setSuccessMsg('Announcement deleted.');
    } catch (err) {
      console.error('Error deleting announcement:', err);
      setError(err.message);
    }
  };

  const handleToggleAnnouncementActive = async (announcement) => {
    setError(null);
    try {
      const saveFn = httpsCallable(functions, 'saveNewsletterAnnouncement');
      await saveFn({
        id: announcement.id,
        title: announcement.title,
        body: announcement.body,
        active: !announcement.active,
      });
      setAnnouncements(prev => prev.map(a =>
        a.id === announcement.id ? { ...a, active: !a.active } : a
      ));
    } catch (err) {
      console.error('Error toggling announcement:', err);
      setError(err.message);
    }
  };

  const handleMoveAnnouncement = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= announcements.length) return;

    const newOrder = [...announcements];
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    setAnnouncements(newOrder);

    setReordering(true);
    try {
      const reorderFn = httpsCallable(functions, 'reorderNewsletterAnnouncements');
      await reorderFn({ orderedIds: newOrder.map(a => a.id) });
    } catch (err) {
      console.error('Error reordering announcements:', err);
      setError(err.message);
      await fetchAnnouncements(); // Revert on error
    } finally {
      setReordering(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    const date = ts.toDate ? ts.toDate() : new Date(ts._seconds * 1000);
    return format(date, 'MMM d, yyyy h:mm a');
  };

  if (loading) {
    return (
      <div className="newsletter-loading-state">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading newsletter settings...</p>
      </div>
    );
  }

  return (
    <div className="newsletter-tab-content">
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {/* Toggle + Config */}
      <div className="newsletter-config-card">
        <div className="newsletter-toggle-row">
          <div className="newsletter-toggle-info">
            <span className="newsletter-toggle-label">Enable Newsletter</span>
            <span className="newsletter-toggle-desc">Automated weekly email with upcoming events</span>
          </div>
          <div className="newsletter-toggle-control">
            {toggling && <FontAwesomeIcon icon={faSpinner} spin className="newsletter-toggle-spinner" />}
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                checked={formEnabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={toggling}
              />
            </div>
          </div>
        </div>

        {formEnabled && (
          <div className="newsletter-enabled-content">
            <button
              className="newsletter-config-toggle"
              onClick={() => setShowConfig(!showConfig)}
              type="button"
            >
              <FontAwesomeIcon icon={faCog} />
              <span>Configure</span>
              <FontAwesomeIcon icon={showConfig ? faChevronUp : faChevronDown} className="newsletter-config-chevron" />
            </button>

            {showConfig && (
              <div className="newsletter-config-fields">
                <div className="newsletter-form-row">
                  <div className="newsletter-form-group">
                    <label>Send Day</label>
                    <select value={formSendDay} onChange={(e) => setFormSendDay(parseInt(e.target.value))}>
                      {DAY_NAMES.map((day, i) => (
                        <option key={i} value={i}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div className="newsletter-form-group">
                    <label>Send Hour{orgTimezone ? ` (${orgTimezone.replace(/_/g, ' ')})` : ''}</label>
                    <select value={formSendHour} onChange={(e) => setFormSendHour(parseInt(e.target.value))}>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="newsletter-form-group">
                    <label>Event Lookahead (days)</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={formLookaheadDays}
                      onChange={(e) => setFormLookaheadDays(parseInt(e.target.value) || 14)}
                    />
                  </div>
                </div>

                <div className="newsletter-form-group">
                  <label>Custom Introduction (optional)</label>
                  <textarea
                    rows={2}
                    value={formCustomIntro}
                    onChange={(e) => setFormCustomIntro(e.target.value)}
                    placeholder="Add a personal message at the top of each newsletter..."
                  />
                </div>

                <div className="newsletter-form-group">
                  <label>Custom Footer (optional)</label>
                  <textarea
                    rows={2}
                    value={formCustomFooter}
                    onChange={(e) => setFormCustomFooter(e.target.value)}
                    placeholder="Add a closing message or contact info..."
                  />
                </div>

                <div className="newsletter-form-group">
                  <label>Reply-To Email (optional)</label>
                  <input
                    type="email"
                    value={formReplyToEmail}
                    onChange={(e) => setFormReplyToEmail(e.target.value)}
                    placeholder={orgContactEmail || 'contact@churchaware.com'}
                  />
                  <span className="newsletter-field-help">
                    When subscribers reply to your newsletter, their response will be sent to this address.
                    {orgContactEmail
                      ? ` Defaults to your organization email (${orgContactEmail}) if left blank.`
                      : ' If left blank, replies go to contact@churchaware.com.'}
                  </span>
                </div>

                <div className="newsletter-proof-config">
                  <div className="newsletter-proof-row">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="proofEnabled"
                        checked={formProofEnabled}
                        onChange={(e) => setFormProofEnabled(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="proofEnabled">
                        Enable Proofreader Preview
                      </label>
                    </div>
                    {formProofEnabled && (
                      <div className="newsletter-proof-lead">
                        <label>Send Proof</label>
                        <select
                          value={formProofLeadHours}
                          onChange={(e) => setFormProofLeadHours(parseInt(e.target.value))}
                        >
                          <option value={24}>24 hours before</option>
                          <option value={48}>48 hours before</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <span className="newsletter-proof-desc">
                    Sends a preview email to designated proofreaders before the scheduled send.
                  </span>
                </div>

                <div className="newsletter-form-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                    ) : (
                      <><FontAwesomeIcon icon={faSave} /> Save Settings</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {config?.enabled && config?.sendgridGroupId && (
              <div className="newsletter-send-actions">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSendTest}
                  disabled={sendingTest || sendingNow}
                >
                  {sendingTest ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Sending...</>
                  ) : (
                    <><FontAwesomeIcon icon={faPaperPlane} /> Send Test to {currentUser?.email}</>
                  )}
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSendNow}
                  disabled={sendingNow || sendingTest}
                >
                  {sendingNow ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Sending...</>
                  ) : (
                    <><FontAwesomeIcon icon={faPaperPlane} /> Send Now</>
                  )}
                </button>

                {config?.lastSentAt && (
                  <span className="newsletter-last-sent">
                    Last sent: {formatTimestamp(config.lastSentAt)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Everything below only shows when newsletter is enabled */}
      {config?.enabled && (
        <>
          {/* Email Preview */}
          <div className="newsletter-section-card">
            <div className="newsletter-section-card-header">
              <FontAwesomeIcon icon={faEye} /> Email Preview
            </div>
            <div className="newsletter-preview-actions">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handlePreview}
                disabled={previewLoading}
              >
                {previewLoading ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</>
                ) : (
                  <><FontAwesomeIcon icon={faEye} /> {previewHtml ? 'Refresh Preview' : 'Load Preview'}</>
                )}
              </button>
            </div>
            {previewHtml && (
              <iframe
                className="newsletter-preview-frame"
                srcDoc={previewHtml}
                sandbox="allow-same-origin"
                title="Newsletter preview"
                onLoad={handleIframeLoad}
              />
            )}
          </div>

          {/* Announcements */}
          <div className="newsletter-section-card">
            <div className="newsletter-section-card-header">
              <FontAwesomeIcon icon={faBullhorn} /> Announcements
              {announcements.length > 0 && <Badge bg="success" className="ms-1">{announcements.length}</Badge>}
            </div>
            <p className="newsletter-link-desc">
              Add reusable announcements that appear in every newsletter. Toggle them on or off per send.
            </p>

            <div className="newsletter-add-announcement-btn">
              {!announcementForm && (
                <button
                  className="btn btn-sm btn-outline-success"
                  onClick={() => setAnnouncementForm({ title: '', body: '', active: true })}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Announcement
                </button>
              )}
            </div>

            {announcementForm && (
              <form className="newsletter-announcement-form" onSubmit={handleSaveAnnouncement}>
                <div className="newsletter-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={announcementForm.title}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Weekly Tuesday Bible Studies on Zoom"
                    maxLength={200}
                    required
                    disabled={announcementSaving}
                  />
                </div>
                <div className="newsletter-form-group">
                  <label>Body</label>
                  <textarea
                    rows={3}
                    value={announcementForm.body}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, body: e.target.value }))}
                    placeholder="Full announcement text..."
                    maxLength={2000}
                    required
                    disabled={announcementSaving}
                  />
                </div>
                <div className="newsletter-form-group">
                  <label>Expires (optional)</label>
                  <input
                    type="date"
                    value={announcementForm.expiresAt || ''}
                    onChange={(e) => setAnnouncementForm(prev => ({ ...prev, expiresAt: e.target.value || null }))}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={announcementSaving}
                  />
                </div>
                <div className="newsletter-announcement-form-actions">
                  <button
                    type="submit"
                    className="btn btn-sm btn-success"
                    disabled={announcementSaving || !announcementForm.title?.trim() || !announcementForm.body?.trim()}
                  >
                    {announcementSaving ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                    ) : (
                      <><FontAwesomeIcon icon={faSave} /> {announcementForm.id ? 'Update' : 'Add'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setAnnouncementForm(null)}
                    disabled={announcementSaving}
                  >
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                  </button>
                </div>
              </form>
            )}

            {announcementsLoading && announcements.length === 0 ? (
              <div className="newsletter-inline-empty">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading announcements...
              </div>
            ) : announcements.length === 0 && !announcementForm ? (
              <div className="newsletter-inline-empty">
                No announcements yet. Add one to include it in your newsletters.
              </div>
            ) : (
              <div className="newsletter-announcements-list">
                {announcements.map((ann, index) => (
                  <div key={ann.id} className={`newsletter-announcement-card ${ann.active ? '' : 'inactive'}`}>
                    <div className="newsletter-announcement-card-header">
                      <div className="newsletter-announcement-order-btns">
                        <button
                          className="btn"
                          onClick={() => handleMoveAnnouncement(index, -1)}
                          disabled={index === 0 || reordering}
                          title="Move up"
                        >
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                        <button
                          className="btn"
                          onClick={() => handleMoveAnnouncement(index, 1)}
                          disabled={index === announcements.length - 1 || reordering}
                          title="Move down"
                        >
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                      </div>
                      <span className="newsletter-announcement-title">{ann.title}</span>
                      <div className="newsletter-announcement-actions">
                        <div className="form-check form-switch" title={ann.active ? 'Active' : 'Inactive'}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={ann.active}
                            onChange={() => handleToggleAnnouncementActive(ann)}
                          />
                        </div>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setAnnouncementForm({
                            id: ann.id,
                            title: ann.title,
                            body: ann.body,
                            active: ann.active,
                            expiresAt: ann.expiresAt ? new Date(ann.expiresAt._seconds * 1000).toISOString().split('T')[0] : null,
                          })}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteAnnouncement(ann.id)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </div>
                    <div className="newsletter-announcement-body-preview">{ann.body}</div>
                    {ann.expiresAt && (
                      <div className="newsletter-announcement-expires">
                        {new Date(ann.expiresAt._seconds * 1000) < new Date()
                          ? `Expired ${new Date(ann.expiresAt._seconds * 1000).toLocaleDateString()}`
                          : `Expires ${new Date(ann.expiresAt._seconds * 1000).toLocaleDateString()}`
                        }
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Signup Link */}
          {orgSlug && (
            <div className="newsletter-section-card">
              <div className="newsletter-section-card-header">
                <FontAwesomeIcon icon={faLink} /> Signup Link
              </div>
              <p className="newsletter-link-desc">Share this URL so anyone can subscribe — no account needed.</p>
              <div className="newsletter-link-row">
                <code className="newsletter-link-url">https://www.churchaware.com/newsletter/{orgSlug}</code>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleCopyLink}
                >
                  <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} />
                  {copied ? ' Copied!' : ' Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Subscribers */}
          <div className="newsletter-section-card">
            <div className="newsletter-section-card-header">
              Subscribers
              {config?.subscriberCount > 0 && <Badge bg="primary" className="ms-1">{config.subscriberCount}</Badge>}
            </div>
            <form className="newsletter-add-subscriber-form" onSubmit={handleAddSubscriber}>
              <input
                type="email"
                placeholder="Email address"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                required
                disabled={adding}
              />
              <input
                type="text"
                placeholder="First name (optional)"
                value={addFirstName}
                onChange={(e) => setAddFirstName(e.target.value)}
                disabled={adding}
              />
              <input
                type="text"
                placeholder="Last name (optional)"
                value={addLastName}
                onChange={(e) => setAddLastName(e.target.value)}
                disabled={adding}
              />
              <button type="submit" className="btn btn-sm btn-primary" disabled={adding}>
                {adding ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPlus} /> Add</>}
              </button>
            </form>

            {subscribersLoading && subscribers.length === 0 ? (
              <div className="newsletter-inline-empty">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading subscribers...
              </div>
            ) : subscribers.length === 0 ? (
              <div className="newsletter-inline-empty">
                No subscribers yet. Share your signup link to start building your list.
              </div>
            ) : (
              <>
                <div className="newsletter-subscriber-filters">
                  <input
                    type="text"
                    className="newsletter-subscriber-search"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="newsletter-filter-group">
                    <label>Member</label>
                    <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="member">Members</option>
                      <option value="nonmember">Non-members</option>
                    </select>
                  </div>
                  <div className="newsletter-filter-group">
                    <label>Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>

                {(searchTerm || memberFilter !== 'all' || statusFilter !== 'all') && (
                  <div className="newsletter-filter-summary">
                    Showing {filteredSubscribers.length} of {subscribers.length}
                  </div>
                )}
                {subscribersCapped && (
                  <div className="newsletter-filter-summary">
                    Showing first {subscribers.length} subscribers.
                  </div>
                )}

                {filteredSubscribers.length === 0 ? (
                  <div className="newsletter-inline-empty">
                    No subscribers match these filters.{' '}
                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={clearSubscriberFilters}
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="newsletter-subscribers-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Proofreader</th>
                          <th>Subscribed</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubscribers.map(sub => (
                          <tr key={sub.id}>
                            <td>
                              {sub.email}
                              {sub.memberId && (
                                <Badge bg="info" className="ms-1">Member</Badge>
                              )}
                            </td>
                            <td>{[sub.firstName, sub.lastName].filter(Boolean).join(' ') || '-'}</td>
                            <td>
                              <Badge bg={sub.status === 'confirmed' ? 'success' : 'warning'}>
                                {sub.status}
                              </Badge>
                              {sub.status === 'pending' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success newsletter-confirm-subscriber-btn"
                                  onClick={() => handleConfirmSubscriber(sub.id)}
                                  disabled={confirmingIds.has(sub.id)}
                                  title="Confirm subscriber"
                                >
                                  {confirmingIds.has(sub.id) ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                  )}
                                </button>
                              )}
                            </td>
                            <td>
                              {sub.status === 'confirmed' ? (
                                <div className="form-check form-switch newsletter-proofreader-toggle">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={!!sub.proofreader}
                                    onChange={() => handleToggleProofreader(sub.id, !!sub.proofreader)}
                                  />
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{formatTimestamp(sub.createdAt)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveSubscriber(sub.id)}
                                title="Remove subscriber"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Send History */}
          <div className="newsletter-section-card">
            <div className="newsletter-section-card-header">Send History</div>

            {historyLoading && historyItems.length === 0 ? (
              <div className="newsletter-inline-empty">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading history...
              </div>
            ) : historyItems.length === 0 ? (
              <div className="newsletter-inline-empty">
                No newsletters sent yet. History will appear here after the first send.
              </div>
            ) : (
              <>
                <div className="newsletter-history-list">
                  {historyItems.map(item => {
                    const hasEngagement = item.delivered > 0 || item.opens > 0 || item.clicks > 0;
                    const openRate = item.delivered > 0 && item.uniqueOpens > 0
                      ? Math.round((item.uniqueOpens / item.delivered) * 100)
                      : null;
                    const isExpanded = expandedHistoryId === item.id;

                    return (
                      <div key={item.id} className={`newsletter-history-card ${isExpanded ? 'expanded' : ''}`}>
                        <div
                          className="newsletter-history-header"
                          onClick={() => handleToggleHistoryDetail(item.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="newsletter-history-date">{formatTimestamp(item.sentAt)}</span>
                          <div className="newsletter-history-header-right">
                            {item.type === 'proof' && <Badge bg="warning" text="dark">Proof</Badge>}
                            <Badge bg={item.status === 'sent' ? 'success' : 'danger'}>{item.status}</Badge>
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              className="newsletter-history-chevron"
                            />
                          </div>
                        </div>
                        <div className="newsletter-history-stats">
                          <span><strong>{item.recipientCount}</strong> recipients</span>
                          <span><strong>{item.eventCount}</strong> events</span>
                        </div>
                        {hasEngagement && (
                          <div className="newsletter-engagement-row">
                            <FontAwesomeIcon icon={faChartBar} className="newsletter-engagement-icon" />
                            {item.delivered > 0 && (
                              <span className="newsletter-engagement-stat">{item.delivered} delivered</span>
                            )}
                            {item.uniqueOpens > 0 && (
                              <span className="newsletter-engagement-stat">
                                {item.uniqueOpens} opened{openRate !== null ? ` (${openRate}%)` : ''}
                              </span>
                            )}
                            {item.clicks > 0 && (
                              <span className="newsletter-engagement-stat">{item.clicks} clicks</span>
                            )}
                            {item.bounces > 0 && (
                              <span className="newsletter-engagement-stat newsletter-engagement-warning">
                                {item.bounces} bounced
                              </span>
                            )}
                            {item.dropped > 0 && (
                              <span className="newsletter-engagement-stat newsletter-engagement-warning">
                                {item.dropped} dropped
                              </span>
                            )}
                            {item.spamReports > 0 && (
                              <span className="newsletter-engagement-stat newsletter-engagement-warning">
                                {item.spamReports} spam
                              </span>
                            )}
                            {(item.unsubscribes > 0 || item.groupUnsubscribes > 0) && (
                              <span className="newsletter-engagement-stat newsletter-engagement-warning">
                                {(item.unsubscribes || 0) + (item.groupUnsubscribes || 0)} unsub
                              </span>
                            )}
                          </div>
                        )}
                        {item.eventTitles?.length > 0 && (
                          <div className="newsletter-history-events">
                            {item.eventTitles.join(', ')}
                          </div>
                        )}
                        {item.error && (
                          <div className="newsletter-history-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} /> {item.error}
                          </div>
                        )}

                        {isExpanded && (
                          <div className="newsletter-history-detail">
                            {historyEventsLoading ? (
                              <div className="newsletter-inline-empty">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading event data...
                              </div>
                            ) : historyEvents.length === 0 ? (
                              <div className="newsletter-inline-empty">
                                No engagement data yet. Events appear as recipients interact with the email.
                              </div>
                            ) : (
                              <div className="newsletter-events-table">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Email</th>
                                      <th>Delivered</th>
                                      <th>Opened</th>
                                      <th>Clicked</th>
                                      <th>Bounced</th>
                                      <th>Unsub</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {buildRecipientSummary(historyEvents).map(r => (
                                      <tr key={r.email}>
                                        <td>{r.email}</td>
                                        <td className={`newsletter-status-cell ${r.delivered ? 'yes' : ''}`}>
                                          {r.delivered ? '\u2713' : '\u2014'}
                                        </td>
                                        <td className={`newsletter-status-cell ${r.opened ? 'yes' : ''}`}>
                                          {r.opened ? '\u2713' : '\u2014'}
                                        </td>
                                        <td className={`newsletter-status-cell ${r.clicked ? 'yes' : ''}`}>
                                          {r.clicked ? '\u2713' : '\u2014'}
                                        </td>
                                        <td className={`newsletter-status-cell ${r.bounced ? 'bounced' : ''}`}>
                                          {r.bounced ? '\u2717' : '\u2014'}
                                        </td>
                                        <td className={`newsletter-status-cell ${r.unsubscribed ? 'bounced' : ''}`}>
                                          {r.unsubscribed ? '\u2717' : '\u2014'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {historyHasMore && (
                  <div className="newsletter-load-more">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => fetchHistory(false)}
                      disabled={historyLoading}
                    >
                      {historyLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default NewsletterTab;
